<script setup lang="ts">
//#region import
import { computed, ref, watch } from 'vue';
import { uniq, cloneDeep, isEqual, omit } from 'lodash-es';
import { onBeforeRouteLeave } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useLs } from '@/setup/vueLs';
import { useSessionAction } from '@/composables/useSessionAction';
import { useError } from '@/composables/useError';
import { useApi } from '@/api';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useTypedStore } from '@/store';
import { useEventBus } from '@/setup/eventBus';
import { typeGuard, type ErrorResponse, type ToErrorMap } from '@/types/core';
import { useInputUtils } from '@/composables/useInputUtils';
import LanguagesSelect from '@/components/UIElements/LanguagesSelect.vue';
import type { GetControlTowerFinalPagesBySiteIdResponse, PutControlTowerFinalPagesBySiteIdData } from '@/types/openapi-ts';
//#endregion

//#region types
type PutControlTowerFinalPagesBySiteIdErrors = ToErrorMap<PutControlTowerFinalPagesBySiteIdData['body']>;
//#endregion

//#region const
const LS_KEY = 'settings/data';
//#endregion

//#region composables
const store = useTypedStore();
const ls = useLs();
const { t } = useI18n();
const { onSessionUnlock } = useSessionAction();
const api = useApi();
const eventBus = useEventBus();
const { getInputValue } = useInputUtils();
const popupManager = usePopupManager();
//#endregion

//#region state
const tableData = ref<GetControlTowerFinalPagesBySiteIdResponse['payload']['site_final_page_urls']>([]);
const oldData = ref<GetControlTowerFinalPagesBySiteIdResponse['payload']['site_final_page_urls']>([]);
const siteId = ref('');
const timer = ref<NodeJS.Timeout>();
const isEdit = ref(false);
const isAvailableClick = ref(true);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const errors = ref<NonNullable<PutControlTowerFinalPagesBySiteIdErrors['site_final_page_urls']>>([]);
//TODO: переделать, а после переместить
const { getError } = useError({ errors });
//#endregion

//#region computed
const brands = computed(() => store.getters['misc/brands']);
const adminInfo = computed(() => store.getters['auth/adminInfo']);
const customerLanguages = computed(() => store.getters['misc/customerLanguages']);
const supportedLanguages = computed(() => store.getters['reports/supportedLanguages']);

const languagesSelect = computed(() => {
  return customerLanguages.value
    .filter((item) => !headers.value.includes(item.code))
    .map((item) => ({
      ...item,
      param: item.code,
      label: `${item.name} (${item.name_native})`,
    }));
});

const mappedLanguages = computed(() => {
  return customerLanguages.value.reduce((acc, { code, name }) => {
    acc[code] = name;
    return acc;
  }, {} as Record<string, string>);
});

const headers = computed(() => {
  return uniq(tableData.value.map(({ names }) => Object.keys(names)).flat())
    .filter((el) => supportedLanguages.value.includes(el))
    .sort((a, b) => {
      if (a === 'en') {
        return -1;
      }
      if (b === 'en') {
        return 1;
      }

      return mappedLanguages.value[a]!.localeCompare(mappedLanguages.value[b]!);
    });
});

const activeBrand = computed(() => {
  const { name = '', favicon_base64 = '' } = brands.value.find((brand) => brand.id === siteId.value) || {};

  return {
    name,
    favicon_base64,
  };
});
//#endregion

//#region watch
watch(isEdit, (newValue) => {
  errors.value = [];
  isAvailableClick.value = false;
  clearTimeout(timer.value);

  timer.value = setTimeout(() => {
    isAvailableClick.value = true;
  }, 200);

  if (newValue) {
    oldData.value = cloneDeep(tableData.value);
  } else {
    tableData.value = cloneDeep(oldData.value);
    oldData.value = [];
  }
});
//#endregion

//#region methods

const handleSave = () => {
  errors.value = [];
  ls.set(LS_KEY, JSON.stringify({
    [adminInfo.value.id]: {
      [siteId.value!]: tableData.value,
    },
  }));

  return api.putFinalPages({ siteId: siteId.value! }, {
    site_final_page_urls: tableData.value,
  }).then(() => {
    ls.remove(LS_KEY);
    oldData.value = cloneDeep(tableData.value);
    isEdit.value = false;
  }).catch((e) => {
    if(typeGuard<ErrorResponse<PutControlTowerFinalPagesBySiteIdErrors>>(e)){
      errors.value = e.data.errors?.site_final_page_urls || [];
    }
    
    errors.value.some((url: any) => Object.values(url.names).some((name: any) => {
      if (name[0].message) {
        eventBus.$emit('notify-event', {
          type: 'error',
          text: name[0].message,
        });
        return true;
      }
      return false;
    }));
    throw e;
  });
};

const checkIsEmptySomeLanguage = (names: string[]) => {
  return Object.values(names).some((name) => !name);
};

const handleAddUrl = () => {
  const data = {
    page_url: '',
    names: headers.value.reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, { en: '' } as Record<string, string>),
  };

  tableData.value = [data, ...tableData.value];
};

const handleAddLanguage = (key: string) => {
  tableData.value = tableData.value.map(({ names, ...restParams }) => ({
    ...restParams,
    names: {
      ...names,
      [key]: '',
    },
  }));
};

const handleDeleteLanguage = (key: string) => {
  errors.value = [];

  tableData.value = tableData.value.map(({ names, ...restParams }) => ({
    ...restParams,
    names: omit(names, key),
  }));
};

const handleDelete = (index: number) => {
  errors.value = [];

  const copiedData = cloneDeep(tableData.value);

  copiedData.splice(index, 1);

  if (isEdit.value) {
    tableData.value = cloneDeep(copiedData);
    return Promise.resolve();
  }

  return api.putFinalPages({ siteId: siteId.value! }, {
    site_final_page_urls: copiedData,
  }).then(() => initData());
};

const onSelectBrand = (id: string) => {
  siteId.value = id;
  errors.value = [];
  isEdit.value = false;
  initData();
};

const handleChangeBrand = (id: string) => {
  if (isEdit.value && !isEqual(tableData.value, oldData.value)) {
    const confirmPopup = popupManager.confirm.open({
      data: {
        title: t('settings.predefinedPage.confirm.unsavedChanges.title'),
        message: t('settings.predefinedPage.confirm.unsavedChanges.msg'),
        bodyIcon: 'exclamation-triangle',
        bodyIconColor: 'var(--text-negative)',
        isAlternative: true,
      },
      listeners: {
        success: async () => {
          const context = await confirmPopup;
          try {
            await handleSave();
            onSelectBrand(id);
          } catch (error) {
            window.console.error(error);
          }
          context.handleClose();
        },
        alternative: async () => {
          const context = await confirmPopup;
          onSelectBrand(id);
          context.handleClose();
        },
      },
    });
  } else {
    onSelectBrand(id);
  }
};

const handleDeleteColumn = (key: string) => {
  const popupManager = usePopupManager();
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('settings.predefinedPage.confirm.deleteUrl.title', { text: mappedLanguages.value[key] }),
      message: t('settings.predefinedPage.confirm.deleteUrl.msg', { text: mappedLanguages.value[key], brandName: activeBrand.value.name }),
      bodyIcon: 'trash-alt',
      bodyIconColor: 'var(--text-negative)',
      confirmIcon: 'trash-alt',
      confirmTitle: t('settings.tokens.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        handleDeleteLanguage(key);
        context.handleClose();
      },
    },
  });
};

const handleDeleteRow = (
  row: GetControlTowerFinalPagesBySiteIdResponse['payload']['site_final_page_urls'][number], 
  index: number,
) => {
  const popupManager = usePopupManager();
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('settings.predefinedPage.confirm.deleteUrl.title', { text: row.page_url }),
      message: t('settings.predefinedPage.confirm.deleteUrl.msg', { text: row.page_url, brandName: activeBrand.value.name }),
      bodyIcon: 'trash-alt',
      bodyIconColor: 'var(--text-negative)',
      confirmIcon: 'trash-alt',
      confirmTitle: t('settings.tokens.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        await handleDelete(index);
        context.handleClose();
      },
    },
  });
};

const initData = () => {
  if(!isFirstLoading.value) {
    isDataLoading.value = true;
  }
  api.getFinalPages({ siteId: siteId.value! }).then((res) => {
    const { site_final_page_urls } = res.data.payload;
    tableData.value = site_final_page_urls;
  }).finally(() => {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  });
};
//#endregion

//#region hooks
onBeforeRouteLeave((to, from, next) => {
  if (isEdit.value && !isEqual(tableData.value, oldData.value)) {
    const popupManager = usePopupManager();
    const confirmPopup = popupManager.confirm.open({
      data: {
        title: t('settings.predefinedPage.confirm.unsavedChanges.title'),
        message: t('settings.predefinedPage.confirm.unsavedChanges.msg'),
        bodyIcon: 'exclamation-triangle',
        bodyIconColor: 'var(--text-negative)',
        isAlternative: true,
      },
      listeners: {
        success: async () => {
          const context = await confirmPopup;
          try {
            await handleSave();
            next();
          } catch (error) {
            window.console.error(error);
          }
          context.handleClose();
        },
        alternative: async () => {
          const context = await confirmPopup;
          next();
          context.handleClose();
        },
      },
    });
  } else {
    next();
  }
});

ls.remove(LS_KEY);

onSessionUnlock.value = async () => {
  const data = JSON.parse(ls.get(LS_KEY))?.adminInfo.value.id || {};
  const [brandId] = Object.keys(data);
  siteId.value = brandId || brands.value[0]!.id;

  await onSelectBrand(siteId.value);
  if (brandId) {
    isEdit.value = true;
    oldData.value = cloneDeep(tableData.value);
    tableData.value = data[siteId.value];
  }
};
siteId.value = brands.value[0]!.id;
initData();
//#endregion
</script>

<template>
  <div class="predefined-page">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('settings.predefinedPage.title') }}
      </template>
      <template v-slot:actions>
        <LanguagesSelect
          v-if="isEdit"
          :languages="languagesSelect"
          :disabled="!languagesSelect.length || !tableData.length"
          @select="handleAddLanguage"
        />
        <UiButton
          v-if="isEdit"
          icon="link"
          @click="handleAddUrl"
        >
          {{ $t('settings.predefinedPage.addUrl') }}
        </UiButton>
        <UiButton
          v-if="!isEdit"
          filled
          icon="pen"
          @click="isEdit = true"
        >
          {{ $t('crm.buttons.edit') }}
        </UiButton>
        <UiButton
          v-if="isEdit"
          type="negative"
          icon="times"
          @click="isEdit = false"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          v-if="isEdit"
          filled
          icon="check"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </template>
    </UiToolbar>
    <BrandsList
      :current-brand="siteId"
      @brand-change="handleChangeBrand"
    />
    <UiTableNew
      v-loading="isDataLoading"
      :data="tableData"
      disable-sort
      lazy-loading
      :class="[
        'predefined-page__table',
        { 'predefined-page__table_edit': isEdit }
      ]"
      is-static
    >
      <template v-slot:append>
        <ElTableColumn
          v-for="header in headers"
          :key="header"
          :label="header"
          :resizable="true"
        >
          <template v-slot:header>
            <div class="predefined-page__table-header">
              <span class="predefined-page__table-header-title">{{ mappedLanguages[header] }}</span>
              <ActionIcon
                v-if="isEdit && header !== 'en'"
                color="inherit"
                icon="trash-alt"
                :tooltip="$t('crm.buttons.delete')"
                @click="handleDeleteColumn(header)"
              />
            </div>
          </template>
          <template v-slot="scope">
            <input
              v-if="isEdit"
              :class="[
                'predefined-page__table-editable-cell',
                { 'predefined-page__table-editable-cell_error': getError([scope.$index, 'names', header]) }
              ]"
              :value="scope.row.names[header]"
              @change="tableData[scope.$index]!.names[header] = getInputValue($event)"
            >
            <div
              v-else
              :class="[
                'predefined-page__table-editable-cell predefined-page__table-editable-cell_view',
                { 'predefined-page__table-editable-cell_error': getError([scope.$index, 'names', header]) }
              ]"
            >
              <span>{{ scope.row.names[header] }}</span>
            </div>
          </template>
        </ElTableColumn>
        <ElTableColumn
          v-if="isEdit"
          width="28"
          align="center"
          label=""
          :resizable="false"
          fixed
        >
          <template v-slot>
            <UiIcon
              name="grip-lines"
              :class="{ 'drag predefined-page__table-grip': isEdit, 'disabled': !isEdit }"
              color="var(--text-body)"
            />
          </template>
        </ElTableColumn>
        <ElTableColumn
          :label="$t('settings.predefinedPage.url')"
          resizable
          fixed
        >
          <template v-slot="scope">
            <input
              v-if="isEdit"
              :class="[
                'predefined-page__table-editable-cell',
                { 'predefined-page__table-editable-cell_error': getError([scope.$index, 'page_url']) }
              ]"
              :value="scope.row.page_url"
              @change="tableData[scope.$index]!.page_url = getInputValue($event)"
            >
            <div
              v-else
              :class="[
                'predefined-page__table-editable-cell predefined-page__table-editable-cell_view',
                { 'predefined-page__table-editable-cell_error': getError([scope.$index, 'page_url']) }
              ]"
            >
              <span>{{ scope.row.page_url }}</span>
              <UiTooltip
                v-if="checkIsEmptySomeLanguage(scope.row.names)"
                :tooltip="$t('settings.predefinedPage.missingTranslations')"
              >
                <UiIcon
                  class="t:ml-4 t:cursor-pointer"
                  name="exclamation-triangle"
                  color="var(--text-negative)"
                />
              </UiTooltip>
            </div>
          </template>
        </ElTableColumn>
        <ElTableColumn 
          :label="$t('settings.tokens.actions')"
          :resizable="false"
          width="74"
          align="center"
          fixed="right"
        >
          <template v-slot="scope">
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('crm.buttons.delete')"
              @click="handleDeleteRow(scope.row, scope.$index)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>

<style lang='scss'>
.predefined-page {
  &__table {
    .el-table__row {
      .el-table__cell {
        padding: 0;

        .cell {
          height: inherit;
          padding: 0;
        }
      }
    }
    &_edit {
      th, tr td {
        &:nth-child(2) {
          position: sticky;
          left: 28px;
          z-index: 2;
        }
      }
    }
  }
  &__table-grip{
    cursor: grab;
  }
  &__table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    max-width: 100%;
  }
  &__table-header-title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__table-editable-cell {
    all: unset;
    padding: 0 8px;
    height: inherit;
    width: 100%;
    overflow: hidden;

    &:focus:read-write {
      box-sizing: border-box;
      border: solid 1px var(--border-focused);
    }

    &_view {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: auto;
      span {
        overflow: hidden;
        text-overflow: ellipsis;
        display: block;
        white-space: nowrap;
      }
    }
    &_error {
      box-sizing: border-box;
      border: solid 1px var(--border-negative);
    }
  }
}
</style>

